import ApplicationController from './application_controller';

// Wires the "Test the AI" form. Four jobs:
//
// 1. When the user picks a different instruction set, swap the work_data
//    textarea to that instruction set's example_input (sans `question`
//    key — the Question field is the source of truth for that). Update
//    the Question field's placeholder to the example question.
//
// 2. Repopulate the version dropdown — "Latest" plus that instruction
//    set's available versions (newest first).
//
// 3. Disable the submit button on form submit so a busy user can't fire
//    multiple inference requests by double-clicking.
//
// 4. Preserve user-customized work_data on dropdown change — only overwrite
//    the textarea if its current contents match one of the known example
//    templates (i.e. the user hasn't touched it).
export default class extends ApplicationController {
  static targets = ['workData', 'description', 'version', 'submit', 'question'];
  static values = {
    examples: Object,
    descriptions: Object,
    versions: Object,
    questionPlaceholders: Object,
  };

  connect() {
    const initial = this.currentInstructionSetName();
    if (initial) this.applySelection(initial);
  }

  updateExample(event) {
    const name = event?.detail?.value ?? event?.currentTarget?.value ?? this.currentInstructionSetName();
    if (name) this.applySelection(name);
  }

  submitting() {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = true;
    this.submitTarget.innerHTML = '<i class="bi bi-hourglass-split mr-1"></i> Running…';
  }

  applySelection(name) {
    this.applyDescription(name);
    this.applyExample(name);
    this.applyVersions(name);
    this.applyQuestionPlaceholder(name);
  }

  applyDescription(name) {
    if (!this.hasDescriptionTarget) return;
    this.descriptionTarget.textContent = this.descriptionsValue[name] || '';
  }

  applyExample(name) {
    const example = this.examplesValue[name];
    if (!example) return;

    // Strip `question` (owned by the Question field) and `files` (owned by the
    // drop zone) — neither belongs in the work_data textarea.
    const workDataExample = { ...example };
    delete workDataExample.question;
    delete workDataExample.files;
    const candidate = Object.keys(workDataExample).length > 0 ? JSON.stringify(workDataExample, null, 2) : '{}';

    const current = this.workDataTarget.value.trim();
    if (!current || current === '{}' || this.isKnownExample(current)) {
      this.workDataTarget.value = candidate;
    }
  }

  applyQuestionPlaceholder(name) {
    if (!this.hasQuestionTarget) return;
    const placeholder = this.questionPlaceholdersValue[name];
    if (placeholder) this.questionTarget.placeholder = placeholder;
  }

  applyVersions(name) {
    if (!this.hasVersionTarget) return;
    const versions = this.versionsValue[name] || [];
    const previousSelection = this.versionTarget.value;

    this.versionTarget.innerHTML = '';

    const latestOpt = document.createElement('option');
    latestOpt.value = '';
    latestOpt.textContent = versions.length ? `Latest (currently ${versions[0]})` : 'Latest';
    this.versionTarget.appendChild(latestOpt);

    versions.forEach((v) => {
      const opt = document.createElement('option');
      opt.value = v;
      opt.textContent = v;
      if (v === previousSelection) opt.selected = true;
      this.versionTarget.appendChild(opt);
    });
  }

  currentInstructionSetName() {
    const hidden = this.element.querySelector('input[name="instruction_set"]');
    return hidden?.value;
  }

  isKnownExample(value) {
    return Object.values(this.examplesValue)
      .map((example) => {
        const rest = { ...example };
        delete rest.question;
        delete rest.files;
        return JSON.stringify(rest, null, 2);
      })
      .includes(value);
  }
}
